/**
 * Hercules private ad campaign.
 * Modules: config, storage, state, timer, cart/gift, ui, analytics.
 * Inactive visitors take the cheap path: one storage read, then exit.
 */
(function () {
  "use strict";

  const STORAGE_KEY = "hrc_campaign_access";
  const EXPIRED_KEY = "hrc_campaign_expired_ids";
  const UTM_STORE_KEY = "hrc_utm_attribution";
  const GIFT_PROP = "_campaign_gift";
  const STATE_VERSION = 1;
  const UTM_TRACKED = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_id",
    "utm_placement",
    "campaign_id",
    "ad_id",
    "fbclid",
    "gclid",
    "gbraid",
    "wbraid",
    "ttclid",
    "msclkid",
  ];

  function now() {
    return Date.now();
  }

  function routesRoot() {
    const r = window.Shopify?.routes?.root || "/";
    return r.endsWith("/") ? r : `${r}/`;
  }

  function toCents(value) {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
  }

  function pad2(n) {
    return String(Math.max(0, Number(n) || 0)).padStart(2, "0");
  }

  function normalizeCode(value) {
    return String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  }

  function codesEqual(a, b) {
    return normalizeCode(a) === normalizeCode(b);
  }

  const MEDIA_GRID_COUNT = 6;
  const MEDIA_TYPE_CAP = 2;

  function foldText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/ı/g, "i")
      .replace(/ş/g, "s")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c");
  }

  function campaignProductKind(item) {
    const blob = foldText(
      [item?.type, item?.handle, item?.title]
        .concat(Array.isArray(item?.tags) ? item.tags : [])
        .join(" "),
    );
    if (blob.includes("dislik") || blob.includes("mouthguard") || blob.includes("agizlik")) {
      return "dislik";
    }
    if (
      blob.includes("kaval") ||
      blob.includes("shin-guard") ||
      blob.includes("shin guard") ||
      blob.includes("kavallik")
    ) {
      return "kaval";
    }
    if (
      blob.includes("kafa-koruyucu") ||
      blob.includes("kafa koruyucu") ||
      blob.includes("head gear") ||
      blob.includes("headgear")
    ) {
      return "kafa";
    }
    if (blob.includes("bandaj") || blob.includes("bandage") || blob.includes("hand wrap")) {
      return "bandaj";
    }
    if (blob.includes("sortu") || blob.includes("shorts") || /\bshort\b/.test(blob)) {
      return "sort";
    }
    if (blob.includes("spor takimi") || blob.includes("tayt") || blob.includes("crop")) {
      return "takim";
    }
    if (blob.includes("havlu") || blob.includes("towel")) {
      return "havlu";
    }
    if (blob.includes("canta") || blob.includes("duffel") || blob.includes("gym bag")) {
      return "canta";
    }
    if (blob.includes("tisort") || blob.includes("t-shirt") || blob.includes("tshirt")) {
      return "tisort";
    }
    if (blob.includes("mma") && (blob.includes("eldiven") || blob.includes("glove"))) {
      return "mma-eldiven";
    }
    if (blob.includes("eldiven") || blob.includes("glove")) {
      return "boks-eldiven";
    }
    return "diger";
  }

  function shuffleInPlace(list) {
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const swap = list[i];
      list[i] = list[j];
      list[j] = swap;
    }
    return list;
  }

  function pickCampaignMedia(items, count, cap) {
    const shuffled = shuffleInPlace(items.slice());
    const picked = [];
    const used = Object.create(null);
    const giftHandle = String(CONFIG.gift.productHandle || "").toLowerCase();
    const giftId = Number(CONFIG.gift.productId) || 0;

    for (const item of shuffled) {
      if (picked.length >= count) break;
      const handle = String(item.handle || "").toLowerCase();
      if (giftHandle && handle === giftHandle) continue;
      if (giftId && Number(item.id) === giftId) continue;
      if (window.HrcGift?.isGiftOnlyProduct?.(item)) continue;
      const kind = campaignProductKind(item);
      if (kind === "dislik") continue;
      if ((used[kind] || 0) >= cap) continue;
      used[kind] = (used[kind] || 0) + 1;
      picked.push(item);
    }
    return picked;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function productTags(product) {
    const tags = product && product.tags;
    if (Array.isArray(tags)) {
      return tags.map((tag) => String(tag).trim().toLowerCase()).filter(Boolean);
    }
    return String(tags || "")
      .split(",")
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  function imageSrc(src, width) {
    if (!src) return "";
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  function collectionProductsUrl(handle, page) {
    return `${routesRoot()}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`;
  }

  async function fetchCollectionProducts(handle) {
    const products = [];
    const seen = new Set();
    for (let page = 1; page <= 20; page += 1) {
      const res = await fetch(collectionProductsUrl(handle, page), { credentials: "same-origin" });
      if (!res.ok) break;
      const data = await res.json();
      const batch = Array.isArray(data.products) ? data.products : [];
      if (!batch.length) break;
      let added = 0;
      batch.forEach((product) => {
        const id = Number(product && product.id);
        if (!id || seen.has(id)) return;
        seen.add(id);
        products.push(product);
        added += 1;
      });
      if (added === 0 || batch.length < 30) break;
    }
    return products;
  }

  function isReelProduct(product, excludedIds) {
    if (!product || !product.handle) return false;
    const images = Array.isArray(product.images) ? product.images : [];
    if (!images.length || !images[0] || !images[0].src) return false;
    const handle = String(product.handle).toLowerCase();
    if (handle.includes("ozel-koleksiyon")) return false;
    const id = Number(product.id);
    if (excludedIds && excludedIds.has(id)) return false;
    const tags = productTags(product);
    if (tags.includes("gift-only") || tags.includes("ozel-koleksiyon") || tags.includes("special-collection")) {
      return false;
    }
    const giftHandle = String(CONFIG.gift.productHandle || "").toLowerCase();
    const giftHandles = (CONFIG.gift.handles || []).map((item) => String(item).toLowerCase());
    if ((giftHandle && handle === giftHandle) || giftHandles.includes(handle)) return false;
    const giftId = Number(CONFIG.gift.productId) || 0;
    if (giftId && id === giftId) return false;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const priced = variants.some((variant) => parseFloat(variant && variant.price) > 0);
    if (!priced && !(parseFloat(product.price) > 0)) return false;
    return true;
  }

  /* Random order with each category spread evenly along the strip, so large groups never cluster. */
  function mixReelProducts(products) {
    const buckets = new Map();
    products.forEach((product) => {
      const kind = campaignProductKind({
        type: product.product_type,
        handle: product.handle,
        title: product.title,
        tags: productTags(product),
      });
      if (!buckets.has(kind)) buckets.set(kind, []);
      buckets.get(kind).push(product);
    });
    const keyed = [];
    buckets.forEach((list, kind) => {
      shuffleInPlace(list).forEach((product, index) => {
        keyed.push({ product, kind, key: (index + Math.random()) / list.length });
      });
    });
    keyed.sort((a, b) => a.key - b.key);
    for (let i = 1; i < keyed.length; i += 1) {
      if (keyed[i].kind !== keyed[i - 1].kind) continue;
      const swap = keyed.findIndex(
        (entry, j) =>
          j > i &&
          entry.kind !== keyed[i - 1].kind &&
          (j + 1 >= keyed.length || keyed[j + 1].kind !== keyed[i].kind) &&
          keyed[j - 1].kind !== keyed[i].kind
      );
      if (swap > -1) [keyed[i], keyed[swap]] = [keyed[swap], keyed[i]];
    }
    return keyed.map((entry) => entry.product);
  }

  function reelCardMarkup(product, eager) {
    const src = imageSrc(product.images[0].src, 800);
    const src320 = imageSrc(product.images[0].src, 320);
    const src480 = imageSrc(product.images[0].src, 480);
    const loading = eager ? "eager" : "lazy";
    return `<div class="hrc-campaign-reel__card" data-hrc-campaign-reel-card><span class="hrc-campaign-reel__frame"><img class="hrc-campaign-reel__img" src="${escapeHtml(src)}" srcset="${escapeHtml(src320)} 320w, ${escapeHtml(src480)} 480w, ${escapeHtml(src)} 800w" sizes="(max-width: 749px) 46vw, 18vw" alt="" width="800" height="1000" loading="${loading}" decoding="async"></span></div>`;
  }

  function readConfig() {
    const raw = window.hrcCampaignConfig || {};
    const discount = raw.discount || {};
    const gift = raw.gift || {};
    const ids = Array.isArray(raw.eligibleProductIds)
      ? raw.eligibleProductIds.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : [];
    const giftProductId = Number(gift.productId) || 0;
    return {
      enabled: raw.enabled === true && window.hrcIsB2B !== true,
      id: String(raw.id || "meta-sep26"),
      source: String(raw.source || "meta"),
      version: Number(raw.version) || STATE_VERSION,
      durationMs: Math.max(60 * 1000, Number(raw.durationMs) || 24 * 60 * 60 * 1000),
      discount: {
        enabled: discount.enabled === true && Number(discount.value) > 0 && Boolean(discount.code),
        type: String(discount.type || "percentage"),
        value: Math.max(0, Math.min(90, Number(discount.value) || 0)),
        code: normalizeCode(discount.code),
      },
      gift: {
        enabled: gift.enabled !== false,
        productHandle: String(gift.productHandle || ""),
        productId: giftProductId,
        variantId: Number(gift.variantId) || 0,
        title: String(gift.title || ""),
        image: String(gift.image || ""),
        url: String(gift.url || ""),
        discountCode: normalizeCode(gift.discountCode),
        maxQuantity: Math.max(1, Number(gift.maxQuantity) || 1),
        handles: Array.isArray(gift.handles) ? gift.handles.map(String).filter(Boolean) : [],
        available: gift.available !== false,
      },
      landingPage: String(raw.landingPage || "/pages/size-ozel"),
      landingHandle: String(raw.landingHandle || "size-ozel"),
      isLandingPage: raw.isLandingPage === true,
      collectionHandle: String(raw.collectionHandle || ""),
      eligibleProductIds: ids.filter((id) => id !== giftProductId),
      eligibleSet: null,
      discoverUrl: String(raw.discoverUrl || "/collections/all"),
      copy: raw.copy && typeof raw.copy === "object" ? raw.copy : {},
    };
  }

  let CONFIG = readConfig();

  function eligibleSet() {
    if (CONFIG.eligibleSet) return CONFIG.eligibleSet;
    CONFIG.eligibleSet = new Set(CONFIG.eligibleProductIds);
    return CONFIG.eligibleSet;
  }

  function t(key, fallback) {
    const val = CONFIG.copy?.[key];
    return val == null || val === "" ? fallback : String(val);
  }

  function formatMoney(cents) {
    if (window.HrcFormatMoney) {
      return window.HrcFormatMoney(cents, { forceDecimals: true });
    }
    const abs = Math.round(Math.abs(Number(cents) || 0));
    const grouped = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${grouped},${String(abs % 100).padStart(2, "0")}TL`;
  }

  function applyPercent(cents, percent) {
    const amount = toCents(cents);
    const pct = Math.max(0, Math.min(90, Number(percent) || 0));
    if (pct <= 0 || amount <= 0) return amount;
    return Math.round((amount * (100 - pct)) / 100);
  }

  /* ------------------------------------------------------------------ */
  /* campaign-storage                                                    */
  /* ------------------------------------------------------------------ */
  const CampaignStorage = {
    get(key) {
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed;
      } catch (_) {
        try {
          window.localStorage.removeItem(key);
        } catch (__) {
          /* ignore */
        }
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (_) {
        return false;
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (_) {
        /* ignore */
      }
    },
  };

  /* ------------------------------------------------------------------ */
  /* campaign-state                                                      */
  /* ------------------------------------------------------------------ */
  function readExpiredIds() {
    const parsed = CampaignStorage.get(EXPIRED_KEY);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(String).filter(Boolean);
  }

  function markExpiredId(id) {
    const ids = readExpiredIds();
    const key = String(id || "");
    if (!key || ids.includes(key)) return;
    ids.push(key);
    CampaignStorage.set(EXPIRED_KEY, ids.slice(-20));
  }

  function clearExpiredId(id) {
    const key = String(id || "");
    const ids = readExpiredIds().filter((item) => item !== key);
    CampaignStorage.set(EXPIRED_KEY, ids);
  }

  function isIdExpired(id) {
    return readExpiredIds().includes(String(id || ""));
  }

  function sanitizeState(raw) {
    if (!raw || typeof raw !== "object") return null;
    const campaignId = String(raw.campaignId || "");
    const startedAt = Number(raw.startedAt);
    const expiresAt = Number(raw.expiresAt);
    if (!campaignId || !Number.isFinite(startedAt) || !Number.isFinite(expiresAt)) {
      return null;
    }
    return {
      campaignId,
      active: raw.active === true,
      startedAt,
      expiresAt,
      source: String(raw.source || CONFIG.source),
      version: Number(raw.version) || STATE_VERSION,
      attribution: raw.attribution && typeof raw.attribution === "object" ? raw.attribution : null,
    };
  }

  function readState() {
    return sanitizeState(CampaignStorage.get(STORAGE_KEY));
  }

  function writeState(state) {
    const next = sanitizeState(state);
    if (!next) {
      CampaignStorage.remove(STORAGE_KEY);
      return null;
    }
    CampaignStorage.set(STORAGE_KEY, next);
    return next;
  }

  function getCampaign() {
    return readState();
  }

  function isCampaignActive() {
    const state = readState();
    if (!state || state.active !== true) return false;
    if (state.campaignId !== CONFIG.id) return false;
    if (now() >= state.expiresAt) return false;
    return true;
  }

  function getCampaignRemainingTime() {
    const state = readState();
    if (!state) return 0;
    return Math.max(0, state.expiresAt - now());
  }

  function splitRemaining(ms) {
    const total = Math.max(0, Math.floor(Number(ms) || 0));
    const secs = Math.floor(total / 1000);
    const days = Math.floor(secs / 86400);
    const hours = Math.floor((secs % 86400) / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const seconds = secs % 60;
    const totalHours = Math.floor(secs / 3600);
    return { days, hours, totalHours, mins, seconds, ms: total };
  }

  function decodeQueryValue(value) {
    try {
      return decodeURIComponent(String(value || "").replace(/\+/g, " "));
    } catch (_) {
      return String(value || "");
    }
  }

  function cleanUtmValue(value) {
    const next = decodeQueryValue(value).trim();
    if (!next || /^\{\{[^}]+\}\}$/.test(next)) return "";
    return next;
  }

  function parseLooseQuery(raw, map) {
    const out = map || {};
    String(raw || "")
      .replace(/^\?/, "")
      .replace(/^#/, "")
      .split("&")
      .forEach((part) => {
        if (!part) return;
        const eq = part.indexOf("=");
        const key = decodeQueryValue(eq === -1 ? part : part.slice(0, eq))
          .trim()
          .toLowerCase();
        if (!key) return;
        let val = eq === -1 ? "" : part.slice(eq + 1);
        val = decodeQueryValue(val);
        const nest = val.search(/\?(utm_|campaign=|hrc_campaign=|fbclid=|gclid=|ttclid=)/i);
        let extra = "";
        if (nest !== -1) {
          extra = val.slice(nest + 1);
          val = val.slice(0, nest);
        }
        val = cleanUtmValue(val);
        if (val) out[key] = val;
        if (extra) parseLooseQuery(extra, out);
      });
    return out;
  }

  function readStoredUtm() {
    try {
      if (window.__hrcUtm?.params && typeof window.__hrcUtm.params === "object") {
        return { ...window.__hrcUtm.params };
      }
    } catch (_) {
      /* ignore */
    }
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(UTM_STORE_KEY) || "null");
      return stored && typeof stored === "object" ? stored : {};
    } catch (_) {
      return {};
    }
  }

  function persistUtm(params) {
    if (!params || typeof params !== "object") return;
    try {
      const stored = readStoredUtm();
      const merged = { ...stored };
      UTM_TRACKED.forEach((key) => {
        if (params[key]) merged[key] = params[key];
      });
      window.sessionStorage.setItem(UTM_STORE_KEY, JSON.stringify(merged));
      window.__hrcUtm = window.__hrcUtm || {};
      window.__hrcUtm.params = { ...(window.__hrcUtm.params || {}), ...merged };
    } catch (_) {
      /* ignore */
    }
  }

  function collectQueryMap(params) {
    const fromUrl = parseLooseQuery(window.location.search || "");
    parseLooseQuery(window.location.hash || "", fromUrl);
    if (fromUrl.redirect && String(fromUrl.redirect).includes("?")) {
      parseLooseQuery(String(fromUrl.redirect).split("?").slice(1).join("?"), fromUrl);
    }
    if (params instanceof URLSearchParams) {
      params.forEach((value, key) => {
        const nested = parseLooseQuery(`${key}=${value}`);
        Object.keys(nested).forEach((k) => {
          if (nested[k] && !fromUrl[k]) fromUrl[k] = nested[k];
        });
      });
    }
    const stored = readStoredUtm();
    const map = { ...stored, ...fromUrl };
    UTM_TRACKED.forEach((key) => {
      if (map[key]) map[key] = cleanUtmValue(map[key]);
      if (!map[key]) delete map[key];
    });
    persistUtm(map);
    return map;
  }

  function attributionFromMap(map) {
    const params = {};
    UTM_TRACKED.forEach((key) => {
      if (map[key]) params[key] = map[key];
    });
    const attribution = {
      source: params.utm_source || "",
      medium: params.utm_medium || "",
      campaign: params.utm_campaign || "",
      content: params.utm_content || "",
      term: params.utm_term || "",
      placement: params.utm_placement || "",
      id: params.utm_id || "",
      campaign_id: params.campaign_id || "",
      ad_id: params.ad_id || "",
      fbclid: params.fbclid || "",
      gclid: params.gclid || "",
      ttclid: params.ttclid || "",
      params,
    };
    const hasValue = Object.values(params).some(Boolean);
    return hasValue ? attribution : null;
  }

  function captureAttribution(params) {
    return attributionFromMap(collectQueryMap(params));
  }

  function mergeAttribution(existing, incoming) {
    if (!existing) return incoming || null;
    if (!incoming) return existing;
    const params = { ...(incoming.params || {}), ...(existing.params || {}) };
    return {
      source: existing.source || incoming.source || "",
      medium: existing.medium || incoming.medium || "",
      campaign: existing.campaign || incoming.campaign || "",
      content: existing.content || incoming.content || "",
      term: existing.term || incoming.term || "",
      placement: existing.placement || incoming.placement || "",
      id: existing.id || incoming.id || "",
      campaign_id: existing.campaign_id || incoming.campaign_id || "",
      ad_id: existing.ad_id || incoming.ad_id || "",
      fbclid: existing.fbclid || incoming.fbclid || "",
      gclid: existing.gclid || incoming.gclid || "",
      ttclid: existing.ttclid || incoming.ttclid || "",
      params,
    };
  }

  function isMalformedSearch(raw) {
    return /(?:^|[?&])[^=&]+=[^&]*\?(?:utm_|campaign=|hrc_campaign=|fbclid=|gclid=)/i.test(
      String(raw || ""),
    );
  }

  function currentSearchMap() {
    const map = parseLooseQuery(window.location.search || "");
    parseLooseQuery(window.location.hash || "", map);
    return map;
  }

  function hasAdLandingSignal(map) {
    const query = map || currentSearchMap();
    if (query.utm_source || query.utm_medium || query.utm_campaign || query.utm_content) return true;
    if (query.utm_placement || query.utm_id || query.utm_term) return true;
    if (query.campaign_id || query.ad_id) return true;
    if (query.fbclid || query.gclid || query.ttclid || query.msclkid) return true;
    return false;
  }

  function readActivationParam() {
    try {
      const map = currentSearchMap();
      const raw = String(map.campaign || map.hrc_campaign || "").trim();
      const token = raw.split("?")[0].trim();
      if (token && token === CONFIG.id) return token;
      return "";
    } catch (_) {
      return "";
    }
  }

  function shouldActivateFromLanding() {
    /*
     * ?campaign=meta-sep26 URL'de varsa herhangi bir sayfada aktifleştir.
     * (ürün sayfasına direkt gelince de çalışsın — Instagram carousel kartları)
     */
    if (readActivationParam()) return true;
    /* UTM sinyalleri yalnızca size-ozel landing page'inde aktifleştirir */
    if (!CONFIG.isLandingPage) return false;
    return hasAdLandingSignal();
  }

  function stripActivationParams() {
    try {
      const url = new URL(window.location.href);
      let changed = false;
      ["campaign", "hrc_campaign"].forEach((key) => {
        const val = String(url.searchParams.get(key) || "").split("?")[0].trim();
        if (val && val === CONFIG.id) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (isMalformedSearch(window.location.search)) {
        const parsed = currentSearchMap();
        UTM_TRACKED.forEach((key) => {
          if (!parsed[key]) return;
          if (url.searchParams.get(key) !== parsed[key]) {
            url.searchParams.set(key, parsed[key]);
            changed = true;
          }
        });
      }
      if (changed && window.history?.replaceState) {
        const qs = url.searchParams.toString();
        window.history.replaceState(null, "", `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`);
      }
    } catch (_) {
      /* ignore */
    }
  }

  function expireCampaign() {
    const state = readState();
    if (state) {
      markExpiredId(state.campaignId);
      writeState({ ...state, active: false });
    }
    document.dispatchEvent(new CustomEvent("hrc:campaign:expired", { detail: { state } }));
    return readState();
  }

  function clearCampaign() {
    CampaignStorage.remove(STORAGE_KEY);
    return null;
  }

  function activateCampaign(id, extras) {
    const campaignId = String(id || CONFIG.id);
    if (!campaignId || campaignId !== CONFIG.id) return readState();

    const force = extras?.force === true;
    if (!force && isIdExpired(campaignId)) return readState();

    const incoming = extras?.attribution || captureAttribution();
    const existing = readState();
    if (existing && existing.campaignId === campaignId) {
      const dead = existing.active === false || now() >= existing.expiresAt;
      if (dead && !force) {
        markExpiredId(campaignId);
        return writeState({ ...existing, active: false });
      }
      if (!dead) {
        const merged = mergeAttribution(existing.attribution, incoming);
        if (merged && JSON.stringify(merged) !== JSON.stringify(existing.attribution || null)) {
          return writeState({ ...existing, attribution: merged });
        }
        return existing;
      }
    }

    if (force) clearExpiredId(campaignId);
    try {
      document.documentElement.classList.remove("hrc-campaign-expired");
      document.documentElement.classList.add("hrc-campaign-hydrated");
    } catch (_) {
      /* ignore */
    }

    const startedAt = now();
    const next = {
      campaignId,
      active: true,
      startedAt,
      expiresAt: startedAt + CONFIG.durationMs,
      source: CONFIG.source,
      version: CONFIG.version,
      attribution: incoming || existing?.attribution || null,
    };
    writeState(next);
    CampaignAnalytics.track("campaign_activated", { campaignId }, { once: true });
    document.dispatchEvent(new CustomEvent("hrc:campaign:activated", { detail: { state: next } }));
    return next;
  }

  function hydrateAndExpire() {
    const state = readState();
    if (!state) return null;
    if (state.campaignId !== CONFIG.id) return state;
    if (state.active && now() >= state.expiresAt) {
      return expireCampaign();
    }
    if (state.active === false) {
      markExpiredId(state.campaignId);
    }
    return readState();
  }

  /* ------------------------------------------------------------------ */
  /* campaign-timer                                                      */
  /* ------------------------------------------------------------------ */
  const timerSubscribers = new Set();
  let timerId = null;

  function publishTimer() {
    const remaining = getCampaignRemainingTime();
    const parts = splitRemaining(remaining);
    timerSubscribers.forEach((fn) => {
      try {
        fn(parts, remaining);
      } catch (_) {
        /* ignore */
      }
    });
    if (remaining <= 0 && readState()?.active) {
      expireCampaign();
      CampaignUI.sync();
      CampaignCart.validate();
    }
  }

  function stopTimer() {
    if (timerId) {
      window.clearInterval(timerId);
      timerId = null;
    }
  }

  function startTimer() {
    if (timerId || !isCampaignActive() || timerSubscribers.size === 0) return;
    publishTimer();
    timerId = window.setInterval(publishTimer, 1000);
  }

  function subscribeTimer(fn) {
    if (typeof fn !== "function") return () => {};
    timerSubscribers.add(fn);
    if (isCampaignActive()) startTimer();
    return () => {
      timerSubscribers.delete(fn);
      if (timerSubscribers.size === 0) stopTimer();
    };
  }

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      stopTimer();
      return;
    }
    if (isCampaignActive() && timerSubscribers.size > 0) startTimer();
  });

  /* ------------------------------------------------------------------ */
  /* campaign-analytics                                                  */
  /* ------------------------------------------------------------------ */
  const CampaignAnalytics = {
    seen(key) {
      try {
        return window.sessionStorage.getItem(key) === "1";
      } catch (_) {
        return false;
      }
    },
    mark(key) {
      try {
        window.sessionStorage.setItem(key, "1");
      } catch (_) {
        /* ignore */
      }
    },
    track(name, payload, opts) {
      try {
        if (!name) return;
        const onceKey = `hrc_campaign_evt_${name}_${CONFIG.id}_${payload?.productId || ""}`;
        if (opts?.once && this.seen(onceKey)) return;
        const detail = {
          event: name,
          campaignId: CONFIG.id,
          ...(payload || {}),
        };
        const state = readState();
        if (state?.attribution) detail.attribution = state.attribution;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: name, ...detail });
        if (typeof window.gtag === "function") {
          window.gtag("event", name, detail);
        }
        if (typeof window.fbq === "function") {
          window.fbq("trackCustom", name, detail);
        }
        document.dispatchEvent(new CustomEvent("hrc:campaign:event", { detail }));
        if (opts?.once) this.mark(onceKey);
      } catch (_) {
        /* ignore */
      }
    },
  };

  /* ------------------------------------------------------------------ */
  /* campaign-cart / campaign-gift                                       */
  /* ------------------------------------------------------------------ */
  function giftPropKey() {
    return window.HrcGift?.PROP || GIFT_PROP;
  }

  function giftDisplayItem(lineItem) {
    const gift = CONFIG.gift || {};
    const image =
      lineItem?.featured_image?.url ||
      lineItem?.image ||
      gift.image ||
      "";
    const title = lineItem?.product_title || lineItem?.title || gift.title || "Hercules Mouthguard";
    return {
      product_title: title,
      title,
      image,
      url: lineItem?.url || gift.url || "",
      featured_image: lineItem?.featured_image || (image ? { url: image, alt: title } : null),
      quantity: 1,
      original_price: 0,
      final_price: 0,
      properties: { [giftPropKey()]: CONFIG.id },
    };
  }

  function emitGiftDisplay(lineItem) {
    try {
      window.dispatchEvent(
        new CustomEvent("hrc:campaign:gift-added", {
          detail: { lineItem: giftDisplayItem(lineItem) },
        }),
      );
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * Cart sync. Shopify owns the pricing; the theme only keeps the cart in the
   * shape the discounts expect:
   *   active + paid items  -> one gift line (qty 1) + campaign codes applied
   *   otherwise            -> no gift line, no campaign codes
   * Customer codes are never touched.
   */
  const CODE_SYNC_KEY = "hrc_campaign_code_sync";

  const CampaignCart = {
    lock: false,
    queued: false,
    ignoreEvent: false,
    giftFailed: false,

    isSystemDiscountCode(code) {
      const value = normalizeCode(code);
      if (!value) return false;
      if (CONFIG.discount.code && codesEqual(value, CONFIG.discount.code)) return true;
      if (CONFIG.gift.discountCode && codesEqual(value, CONFIG.gift.discountCode)) return true;
      return false;
    },

    isGiftItem(item) {
      if (!item) return false;
      if (typeof window.HrcGift?.isGiftLine === "function") return window.HrcGift.isGiftLine(item);
      return Boolean(item.properties?.[GIFT_PROP]);
    },

    isGiftOnlyProduct(item) {
      if (typeof window.HrcGift?.isGiftOnlyProduct === "function") {
        return window.HrcGift.isGiftOnlyProduct(item);
      }
      if (!item) return false;
      if (CONFIG.gift.productId && Number(item.product_id) === Number(CONFIG.gift.productId)) return true;
      return Boolean(CONFIG.gift.variantId && Number(item.variant_id) === Number(CONFIG.gift.variantId));
    },

    isUnauthorizedGift(item) {
      if (typeof window.HrcGift?.isUnauthorizedGift === "function") {
        return window.HrcGift.isUnauthorizedGift(item);
      }
      if (this.isGiftOnlyProduct(item) && !this.isGiftItem(item)) return true;
      return this.isGiftItem(item) && !isCampaignActive();
    },

    isEligibleItem(item) {
      if (!item || this.isGiftItem(item) || this.isGiftOnlyProduct(item)) return false;
      return Number(item.original_price || item.price || 0) > 0;
    },

    async request(path, body) {
      const res = await fetch(`${routesRoot()}${path}`, {
        method: body ? "POST" : "GET",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error(path);
      return res.json();
    },

    fetchCart() {
      return this.request("cart.js");
    },

    changeQty(key, quantity) {
      return this.request("cart/change.js", { id: key, quantity: Math.max(0, Number(quantity) || 0) });
    },

    cartHasDiscount(cart, code) {
      if (!code) return true;
      return (cart?.discount_codes || []).some(
        (d) => codesEqual(d.code, code) && d.applicable !== false,
      );
    },

    cartHasGift(cart) {
      return (cart?.items || []).some((item) => this.isGiftItem(item));
    },

    codesOf(cart) {
      const seen = new Set();
      return (cart?.discount_codes || [])
        .map((d) => String(d?.code || "").trim())
        .filter((code) => {
          const key = normalizeCode(code);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    },

    campaignCodes() {
      const codes = [];
      if (CONFIG.discount.enabled) codes.push(CONFIG.discount.code);
      if (CONFIG.gift.enabled && CONFIG.gift.discountCode) codes.push(CONFIG.gift.discountCode);
      return codes;
    },

    eligibleSignature(cart) {
      return (cart?.items || [])
        .filter((item) => this.isEligibleItem(item))
        .map((item) => `${item.variant_id}:${item.quantity}`)
        .sort()
        .join(",");
    },

    /* At most two attempts per cart shape per minute, so a rejected code cannot reload-loop the cart page. */
    async syncCodes(cart, wantCampaign) {
      const current = this.codesOf(cart);
      const customer = current.filter((code) => !this.isSystemDiscountCode(code));
      const target = wantCampaign ? customer.concat(this.campaignCodes()) : customer;
      const same = (a, b) => a.map(normalizeCode).sort().join(",") === b.map(normalizeCode).sort().join(",");
      if (same(current, target)) return null;

      const key = `${CONFIG.id}|${this.eligibleSignature(cart)}|${target.map(normalizeCode).sort().join(",")}`;
      try {
        let last = null;
        try {
          last = JSON.parse(window.sessionStorage.getItem(CODE_SYNC_KEY) || "null");
        } catch (_) {
          last = null;
        }
        const recent = last && last.key === key && now() - Number(last.at) < 60 * 1000;
        const tries = recent ? Number(last.tries) + 1 : 1;
        if (tries > 2) return null;
        window.sessionStorage.setItem(CODE_SYNC_KEY, JSON.stringify({ key, tries, at: recent ? last.at : now() }));
      } catch (_) {
        /* ignore */
      }
      return this.request("cart/update.js", { discount: target.join(",") });
    },

    async resolveGiftVariant() {
      if (CONFIG.gift.variantId) return CONFIG.gift.variantId;
      const handles = [CONFIG.gift.productHandle].concat(CONFIG.gift.handles || []).filter(Boolean);
      for (const handle of handles) {
        try {
          const product = await this.request(`products/${encodeURIComponent(handle)}.js`);
          const variant = product?.variants?.[0];
          if (!variant?.id) continue;
          CONFIG.gift.variantId = Number(variant.id);
          CONFIG.gift.productId = Number(product.id) || CONFIG.gift.productId;
          return CONFIG.gift.variantId;
        } catch (_) {
          /* try next handle */
        }
      }
      return 0;
    },

    async addGift() {
      if (this.giftFailed) return null;
      const variantId = Number(await this.resolveGiftVariant()) || 0;
      if (!variantId) {
        this.giftFailed = true;
        return null;
      }
      try {
        const data = await this.request("cart/add.js", {
          items: [{ id: variantId, quantity: 1, properties: { [giftPropKey()]: CONFIG.id } }],
        });
        const added = data?.items?.[0] || data;
        CampaignAnalytics.track("campaign_gift_added", {}, { once: true });
        emitGiftDisplay(added);
        return added;
      } catch (_) {
        this.giftFailed = true;
        return null;
      }
    },

    async syncGift(cart, wantGift) {
      const gifts = (cart.items || []).filter((item) => this.isGiftItem(item));
      let mutated = false;
      for (const [index, gift] of gifts.entries()) {
        const qty = wantGift && index === 0 ? 1 : 0;
        if (Number(gift.quantity) === qty) continue;
        await this.changeQty(gift.key, qty);
        mutated = true;
      }
      if (wantGift && !gifts.length && (await this.addGift())) mutated = true;
      return mutated;
    },

    async stampAttribution(cart) {
      const api = window.HrcAttribution;
      if (!api || typeof api.sync !== "function") return;
      try {
        await Promise.race([
          api.sync({
            reason: "campaign-cart",
            attributes: cart && cart.attributes,
          }),
          new Promise((resolve) => window.setTimeout(resolve, 1500)),
        ]);
      } catch (_) {
        /* attribution must not block the cart or checkout */
      }
    },

    async runOnce() {
      let cart = await this.fetchCart();
      let mutated = false;

      for (const item of cart.items || []) {
        if (!this.isUnauthorizedGift(item)) continue;
        await this.changeQty(item.key, 0);
        mutated = true;
      }
      if (mutated) cart = await this.fetchCart();

      const active = isCampaignActive();
      const hasPaid = (cart.items || []).some((item) => this.isEligibleItem(item));

      if (await this.syncGift(cart, active && CONFIG.gift.enabled && hasPaid)) {
        mutated = true;
        cart = await this.fetchCart();
      }

      const updated = await this.syncCodes(cart, active && hasPaid);
      if (updated?.items) {
        mutated = true;
        cart = updated;
      }

      if (active) await this.stampAttribution(cart).catch(() => {});

      if (mutated) {
        this.notify(cart);
        if (document.getElementById("hrc-cart-items-section")) window.location.reload();
      }
      return cart;
    },

    async validate() {
      if (this.lock) {
        this.queued = true;
        return;
      }
      this.lock = true;
      try {
        do {
          this.queued = false;
          await this.runOnce();
        } while (this.queued);
      } catch (_) {
        /* never break the storefront */
      } finally {
        this.lock = false;
      }
    },

    async waitForIdle() {
      const started = now();
      while (this.lock && now() - started < 8000) {
        await new Promise((resolve) => window.setTimeout(resolve, 40));
      }
    },

    async ensureGiftInCart() {
      if (!CONFIG.gift.enabled || !isCampaignActive()) return true;
      this.giftFailed = false;
      await this.waitForIdle();
      await this.validate();
      await this.waitForIdle();
      try {
        return this.cartHasGift(await this.fetchCart());
      } catch (_) {
        return false;
      }
    },

    notify(cart) {
      this.ignoreEvent = true;
      try {
        window.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
        document.dispatchEvent(new CustomEvent("hrc:cart:updated", { detail: { cart } }));
      } catch (_) {
        /* ignore */
      }
      window.setTimeout(() => {
        this.ignoreEvent = false;
      }, 250);
    },

    bind() {
      const onAdded = (event) => {
        const line = event?.detail?.lineItem;
        if (line && isCampaignActive() && isEligibleProductId(line.product_id)) {
          CampaignAnalytics.track("campaign_add_to_cart", { productId: line.product_id });
        }
        this.validate();
      };
      window.addEventListener("cart:updated", (event) => {
        if (!this.ignoreEvent) onAdded(event);
      });
      document.addEventListener("hrc:cart:item-added", onAdded);
    },
  };

  /* ------------------------------------------------------------------ */
  /* campaign-ui                                                         */
  /* ------------------------------------------------------------------ */
  function isEligibleProductId(id) {
    const n = Number(id);
    if (!n) return false;
    if (CONFIG.gift.productId && n === Number(CONFIG.gift.productId)) return false;
    if (typeof window.HrcGift?.isGiftOnlyProduct === "function") {
      if (window.HrcGift.isGiftOnlyProduct({ product_id: n })) return false;
    }
    if (!CONFIG.eligibleProductIds.length) return true;
    return eligibleSet().has(n);
  }

  function currentPdpEligible() {
    const root = document.querySelector(".hrc-pdp");
    if (!root) return false;
    if (root.getAttribute("data-gift-only") === "true") return false;
    if (root.getAttribute("data-hrc-campaign-eligible") === "true") return true;
    return isEligibleProductId(root.getAttribute("data-product-id"));
  }

  function wrapPricing() {
    const api = window.HrcWebDiscount;
    if (!api || typeof api.resolveDisplayPricing !== "function" || api.__hrcCampaignWrapped) return;
    const original = api.resolveDisplayPricing.bind(api);
    api.resolveDisplayPricing = function resolveCampaignPricing(priceCents, compareAtCents) {
      try {
        if (CONFIG.discount.enabled && isCampaignActive() && currentPdpEligible()) {
          const price = toCents(priceCents);
          const discounted = applyPercent(price, CONFIG.discount.value);
          const compare = price > discounted ? price : toCents(compareAtCents);
          return {
            price: discounted,
            compare,
            webDiscount: false,
            campaignDiscount: true,
            percent: CONFIG.discount.value,
            label: t("yourPrice", "Sana Özel"),
          };
        }
      } catch (_) {
        /* fall through */
      }
      return original(priceCents, compareAtCents);
    };
    api.__hrcCampaignWrapped = true;
  }

  function renderTimerParts(root, parts) {
    if (!root) return;
    const set = (unit, value) => {
      const el = root.querySelector(`[data-unit="${unit}"]`);
      if (el) el.textContent = pad2(value);
    };
    const hasDays = Boolean(root.querySelector('[data-unit="days"]'));
    set("days", parts.days);
    set("hours", hasDays ? parts.hours : parts.totalHours);
    set("mins", parts.mins);
    set("secs", parts.seconds);
  }

  function compactClock(parts) {
    if (parts.days > 0 && parts.totalHours >= 48) {
      return `${pad2(parts.days)}:${pad2(parts.hours)}:${pad2(parts.mins)}:${pad2(parts.seconds)}`;
    }
    return `${pad2(parts.totalHours)}:${pad2(parts.mins)}:${pad2(parts.seconds)}`;
  }

  const CampaignUI = {
    unsub: null,

    paintLanding(active, expired) {
      const root = document.querySelector("[data-hrc-campaign-landing]");
      if (!root) return;
      const locked = root.querySelector("[data-hrc-campaign-locked]");
      const expiredEl = root.querySelector("[data-hrc-campaign-expired]");
      const activeEl = root.querySelector("[data-hrc-campaign-active-panel]");
      const timer = root.querySelector("[data-hrc-campaign-timer]");
      if (active) {
        if (locked) locked.hidden = true;
        if (expiredEl) expiredEl.hidden = true;
        if (activeEl) activeEl.hidden = false;
        if (timer) timer.hidden = false;
        CampaignAnalytics.track("campaign_view", {}, { once: true });
      } else if (expired) {
        if (locked) locked.hidden = true;
        if (expiredEl) expiredEl.hidden = false;
        if (activeEl) activeEl.hidden = true;
        if (timer) timer.hidden = true;
      } else {
        if (locked) locked.hidden = false;
        if (expiredEl) expiredEl.hidden = true;
        if (activeEl) activeEl.hidden = true;
        if (timer) timer.hidden = true;
      }
    },

    fillMediaGrid(scope) {
      const root = scope || document;
      root.querySelectorAll("[data-hrc-campaign-media-grid]").forEach((grid) => {
        if (grid.dataset.hrcFilled === "1") return;
        const poolEl =
          grid.parentElement?.querySelector("[data-hrc-campaign-media-pool]") ||
          root.querySelector("[data-hrc-campaign-media-pool]");
        if (!poolEl) return;

        let pool = [];
        try {
          const parsed = JSON.parse(poolEl.textContent || "[]");
          pool = Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return;
        }

        const items = pool.filter((item) => item && item.url && item.image && item.title);
        if (!items.length) return;

        const picked = pickCampaignMedia(items, MEDIA_GRID_COUNT, MEDIA_TYPE_CAP);
        if (!picked.length) return;
        grid.innerHTML = picked
          .map((item, index) => {
            const url = String(item.url);
            const title = String(item.title);
            const image = String(item.image);
            const loading = index < 3 ? "eager" : "lazy";
            return `<li class="hrc-campaign-media-grid__item"><a class="hrc-campaign-media-card" href="${escapeHtml(url)}" aria-label="${escapeHtml(title)}"><span class="hrc-campaign-media-card__frame"><img class="hrc-campaign-media-card__img" src="${escapeHtml(image)}" alt="${escapeHtml(title)}" width="900" height="1125" loading="${loading}"></span></a></li>`;
          })
          .join("");
        grid.dataset.hrcFilled = "1";
      });
    },

    syncReelSpeed(reel) {
      const track = reel.querySelector("[data-hrc-campaign-reel-track]");
      if (!track) return;
      const width = track.scrollWidth;
      if (width < 20) return;
      const mobile = window.matchMedia("(max-width: 749px)").matches;
      const pxPerSec = mobile ? 42 : 68;
      const seconds = Math.max(14, width / 2 / pxPerSec);
      track.style.setProperty("--hrc-reel-duration", `${seconds.toFixed(1)}s`);
    },

    bindReel(reel) {
      if (reel.dataset.hrcReelBound === "1") return;
      reel.dataset.hrcReelBound = "1";
      const sync = () => this.syncReelSpeed(reel);
      sync();
      requestAnimationFrame(sync);
      if ("ResizeObserver" in window) {
        const observer = new ResizeObserver(sync);
        observer.observe(reel);
      }
      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              reel.classList.toggle("is-paused", !entry.isIntersecting);
            });
          },
          { threshold: 0.08 },
        );
        observer.observe(reel);
      }
    },

    renderReel(reel, products) {
      const track = reel.querySelector("[data-hrc-campaign-reel-track]");
      if (!track || !products.length) return;
      const cards = products.map((product, index) => reelCardMarkup(product, index < 4)).join("");
      track.innerHTML = `<div class="hrc-campaign-reel__set" data-hrc-campaign-reel-set>${cards}</div><div class="hrc-campaign-reel__set" aria-hidden="true">${cards}</div>`;
      this.syncReelSpeed(reel);
    },

    async hydrateReel(reel) {
      if (reel.dataset.hrcReelState) return;
      const handle = String(reel.dataset.collectionHandle || "").trim();
      const shown = reel.querySelectorAll("[data-hrc-campaign-reel-set] [data-hrc-campaign-reel-card]").length;
      if (!handle || shown < 1) {
        reel.dataset.hrcReelState = "done";
        return;
      }
      reel.dataset.hrcReelState = "loading";
      try {
        const excludeHandles = String(reel.dataset.excludeHandles || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
        const [products, excludedLists] = await Promise.all([
          fetchCollectionProducts(handle),
          Promise.all(excludeHandles.map((excludeHandle) => fetchCollectionProducts(excludeHandle).catch(() => []))),
        ]);
        const excludedIds = new Set();
        excludedLists.flat().forEach((product) => {
          const id = Number(product && product.id);
          if (id) excludedIds.add(id);
        });
        const eligible = products.filter((product) => isReelProduct(product, excludedIds));
        if (eligible.length) this.renderReel(reel, mixReelProducts(eligible));
      } catch (_) {
        /* keep the server-rendered strip */
      }
      reel.dataset.hrcReelState = "done";
      this.syncReelSpeed(reel);
    },

    fillProductReel() {
      document.querySelectorAll("[data-hrc-campaign-reel]").forEach((reel) => {
        this.bindReel(reel);
        this.hydrateReel(reel);
      });
    },

    enhanceCards(scope) {
      const root = scope || document;
      const cards = root.querySelectorAll("[data-hrc-campaign-grid] hrc-product-card");
      cards.forEach((card) => {
        const catalog = toCents(card.getAttribute("data-base-price") || card.getAttribute("data-price"));
        if (!catalog) return;
        const sale = applyPercent(catalog, CONFIG.discount.value);
        const priceRoot = CONFIG.discount.enabled ? card.querySelector(".hrc-featured-price") : null;
        if (priceRoot) {
          let current = priceRoot.querySelector(".hrc-featured-price-current");
          let old = priceRoot.querySelector(".hrc-featured-price-old");
          if (!current) {
            current = document.createElement("span");
            current.className = "hrc-featured-price-current is-sale";
            priceRoot.appendChild(current);
          }
          current.textContent = formatMoney(sale);
          current.classList.add("is-sale");
          if (!old) {
            old = document.createElement("span");
            old.className = "hrc-featured-price-old";
            priceRoot.appendChild(old);
          }
          old.textContent = formatMoney(catalog);
          old.hidden = false;
        }
        const tags = CONFIG.discount.enabled ? card.querySelector(".hrc-featured-tags") : null;
        if (tags && !tags.querySelector("[data-hrc-campaign-badge]")) {
          tags.querySelectorAll(".hrc-featured-tag-web, .hrc-featured-tag-sale").forEach((el) => {
            el.hidden = true;
          });
          const badge = document.createElement("span");
          badge.className = "hrc-featured-tag hrc-featured-tag-campaign";
          badge.setAttribute("data-hrc-campaign-badge", "true");
          badge.textContent = t("yourPrice", "Sana Özel");
          tags.insertBefore(badge, tags.firstChild);
        }
        const footer = card.querySelector(".hrc-featured-footer");
        if (footer && CONFIG.gift.enabled && !footer.querySelector("[data-hrc-campaign-gift]")) {
          const gift = document.createElement("p");
          gift.className = "hrc-campaign-card-gift";
          gift.setAttribute("data-hrc-campaign-gift", "true");
          gift.textContent = t("giftLine", "+ Dişlik Hediye");
          footer.appendChild(gift);
        }
      });
    },

    paintNav(active) {
      const list = document.querySelector(".navbar__nav-list");
      const existing = document.querySelector("[data-hrc-campaign-nav]");
      if (!active) {
        existing?.remove();
        document.querySelector("[data-hrc-campaign-nav-mobile]")?.closest("li")?.remove();
        document.querySelector("[data-hrc-campaign-nav-mobile]")?.remove();
        return;
      }
      if (list && !existing) {
        const li = document.createElement("li");
        li.className = "navbar__nav-item navbar__nav-item--campaign";
        li.setAttribute("data-hrc-campaign-nav", "true");
        li.innerHTML = `<a class="navbar__nav-link navbar__nav-link--campaign" href="${CONFIG.landingPage}"><span>${t("navLabel", "SIZE ÖZEL")}</span><span class="hrc-campaign-nav__sep" aria-hidden="true"> · </span><span data-hrc-campaign-clock aria-hidden="true"></span></a>`;
        list.appendChild(li);
      }
      const menu = document.querySelector(".hrc-menu__cat-list");
      if (menu && !document.querySelector("[data-hrc-campaign-nav-mobile]")) {
        const li = document.createElement("li");
        li.className = "hrc-menu__cat-item";
        li.innerHTML = `<a class="hrc-menu__cat-btn hrc-campaign-mobile-link" data-hrc-campaign-nav-mobile href="${CONFIG.landingPage}"><span class="hrc-menu__cat-label">${t("navLabel", "SIZE ÖZEL")} · <span data-hrc-campaign-clock aria-hidden="true"></span></span></a>`;
        menu.insertBefore(li, menu.firstChild);
      }
    },

    paintSticky(active) {
      let bar = document.querySelector("[data-hrc-campaign-sticky]");
      const show = active && !CONFIG.isLandingPage && window.matchMedia("(max-width: 989px)").matches;
      if (!show) {
        bar?.remove();
        document.body.classList.remove("hrc-campaign-sticky-on");
        return;
      }
      if (!bar) {
        bar = document.createElement("div");
        bar.className = "hrc-campaign-sticky";
        bar.setAttribute("data-hrc-campaign-sticky", "true");
        bar.innerHTML = `<a class="hrc-campaign-sticky__link" href="${CONFIG.landingPage}"><span class="hrc-campaign-sticky__copy"><span class="hrc-campaign-sticky__label">${t("stickyLabel", "Size Özel")}</span><span class="hrc-campaign-sticky__clock" data-hrc-campaign-clock aria-hidden="true"></span></span><span class="hrc-campaign-sticky__cta">${t("stickyCta", "Teklife Dön")}</span></a>`;
        document.body.appendChild(bar);
      }
      document.body.classList.add("hrc-campaign-sticky-on");
    },

    applyPdpPrices() {
      if (!CONFIG.discount.enabled || !isCampaignActive() || !currentPdpEligible()) return;
      const block = document.querySelector('[id^="hrc-price-block-"]');
      const catalog = toCents(block?.getAttribute("data-base-price"));
      if (!catalog) return;
      const sale = applyPercent(catalog, CONFIG.discount.value);
      const priceEl = document.querySelector('[id^="hrc-price-"]:not([id*="block"])');
      const compareEl = document.querySelector('[id^="hrc-compare-"]');
      const sticky = document.getElementById("hrc-sticky-price");
      const saveEl = document.querySelector('[id^="hrc-save-"]');
      if (priceEl && !priceEl.classList.contains("is-coming-soon")) {
        priceEl.textContent = formatMoney(sale);
        priceEl.classList.add("is-sale", "price-item--sale");
      }
      if (compareEl) {
        compareEl.textContent = formatMoney(catalog);
        compareEl.removeAttribute("hidden");
      }
      if (sticky && !sticky.classList.contains("is-coming-soon")) {
        sticky.textContent = formatMoney(sale);
      }
      if (saveEl) {
        saveEl.textContent = `-%${CONFIG.discount.value}`;
        saveEl.removeAttribute("hidden");
      }
    },

    paintPdp(active) {
      document.querySelector("[data-hrc-campaign-pdp]")?.remove();
      const on = active && currentPdpEligible();
      document.documentElement.classList.toggle("hrc-campaign-pdp", on && CONFIG.discount.enabled);
      if (!on) return;
      this.applyPdpPrices();
      const productId = document.querySelector(".hrc-pdp")?.getAttribute("data-product-id");
      CampaignAnalytics.track("campaign_product_view", { productId }, { once: true });
    },

    paintClocks(parts) {
      document.querySelectorAll("[data-hrc-campaign-timer]").forEach((root) => {
        renderTimerParts(root, parts);
      });
      const clock = compactClock(parts);
      document.querySelectorAll("[data-hrc-campaign-clock]").forEach((el) => {
        el.textContent = clock;
      });
    },

    paintCartGift() {
      document.querySelector("[data-hrc-campaign-gift-visual]")?.remove();
    },

    sync() {
      try {
        const active = isCampaignActive();
        const state = readState();
        const expired = Boolean(state && (state.active === false || now() >= state.expiresAt) && state.campaignId === CONFIG.id);
        document.body.classList.toggle("hrc-campaign-active", active);
        this.paintLanding(active, expired);
        this.fillMediaGrid();
        this.fillProductReel();
        this.paintNav(active);
        this.paintSticky(active);
        this.paintPdp(active);
        this.paintCartGift(active && CONFIG.gift.enabled);
        if (active) {
          this.paintClocks(splitRemaining(getCampaignRemainingTime()));
          if (!this.unsub) {
            this.unsub = subscribeTimer((parts) => this.paintClocks(parts));
          }
        } else {
          this.unsub?.();
          this.unsub = null;
          stopTimer();
        }
      } catch (_) {
        /* fail-safe */
      }
    },
  };

  async function maybeActivateFromCart() {
    if (!CONFIG.isLandingPage) return;
    if (isCampaignActive() || isIdExpired(CONFIG.id)) return;
    if (readActivationParam()) return;
    try {
      const cart = await CampaignCart.fetchCart();
      const hasCode = CONFIG.discount.enabled
        ? CampaignCart.cartHasDiscount(cart, CONFIG.discount.code)
        : false;
      if (hasCode) {
        let attribution = null;
        try {
          attribution = captureAttribution();
        } catch (_) {
          /* ignore */
        }
        activateCampaign(CONFIG.id, { attribution });
      }
    } catch (_) {
      /* ignore */
    }
  }

  function bindCheckoutGuard() {
    let busy = false;

    const checkoutUrl = () => `${routesRoot()}checkout`;

    const isCheckoutClick = (event) =>
      Boolean(
        event.target?.closest?.(
          'a[href*="/checkout"], button[name="checkout"], [name="checkout"], #checkout, .shopify-payment-button, [data-shopify="payment-button"], .hrc-dynamic-checkout-buttons',
        ),
      );

    const isCheckoutSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return false;
      if (form.id === "hrc-cart-notification-form") return true;
      const action = String(form.getAttribute("action") || "");
      if (action.includes("/checkout")) return true;
      const submitter = event.submitter;
      if (submitter?.getAttribute("name") === "checkout") return true;
      if (submitter?.id === "checkout") return true;
      return false;
    };

    const gate = async (event) => {
      if (!CONFIG.gift.enabled || !isCampaignActive()) return false;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (busy) return true;
      busy = true;
      CampaignAnalytics.track("campaign_checkout_started", {}, { once: true });
      try {
        await CampaignCart.ensureGiftInCart();
      } catch (_) {
        /* checkout anyway once gift attempt finished */
      } finally {
        busy = false;
      }
      window.location.assign(checkoutUrl());
      return true;
    };

    document.addEventListener(
      "click",
      (event) => {
        if (!isCheckoutClick(event)) return;
        if (!isCampaignActive()) return;
        if (!CONFIG.gift.enabled) {
          CampaignAnalytics.track("campaign_checkout_started", {}, { once: true });
          return;
        }
        gate(event);
      },
      true,
    );

    document.addEventListener(
      "submit",
      (event) => {
        if (!isCheckoutSubmit(event)) return;
        if (!isCampaignActive()) return;
        if (!CONFIG.gift.enabled) return;
        gate(event);
      },
      true,
    );
  }

  async function boot() {
    try {
      wrapPricing();
      CampaignCart.bind();

      if (!CONFIG.enabled) {
        await CampaignCart.validate();
        return;
      }

      if (shouldActivateFromLanding()) {
        activateCampaign(CONFIG.id, { attribution: captureAttribution(), force: true });
      }
      stripActivationParams();
      hydrateAndExpire();
      await maybeActivateFromCart();
      hydrateAndExpire();

      CampaignUI.sync();
      await CampaignCart.validate();
      bindCheckoutGuard();
      document.addEventListener("hrc:campaign:expired", () => {
        CampaignUI.sync();
        CampaignCart.validate();
      });
      document.addEventListener("variant:change", () => {
        if (isCampaignActive()) CampaignUI.applyPdpPrices();
      });
      window.addEventListener("resize", () => {
        if (isCampaignActive()) CampaignUI.paintSticky(true);
      });
    } catch (_) {
      /* never break the storefront */
    }
  }

  window.HrcCampaign = {
    getCampaign,
    isCampaignActive,
    getCampaignRemainingTime,
    activateCampaign,
    expireCampaign,
    clearCampaign,
    captureAttribution,
    config: () => CONFIG,
    isSystemDiscountCode: (code) => CampaignCart.isSystemDiscountCode(code),
    storage: CampaignStorage,
    validateCampaignCart: () => CampaignCart.validate(),
    ensureCampaignGift: () => CampaignCart.ensureGiftInCart(),
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
